<script lang="ts" setup>
import { computed, onMounted, Ref, ref } from 'vue';
import ClientOnly from '../../modules/client-only';

import type { UploadFeature } from '~/models/upload';
import UploadFeatureComponent from '~/components/upload/UploadFeature.vue';
import CountrySelector from '~/components/ordering/CountrySelector.vue';
import DragAndDropUpload from '~/components/ordering/DragAndDropUpload.vue';
import SharedParts from '~/components/ordering/SharedParts.vue';
import useI18NAsync from '~/composables/i18nAsync';

import vuBtn from '@ds/vuekit/src/components/vu-btn.vue';
import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';

import { useTranslations } from '~/composables/translations';
import { useRouter } from 'vue-router';
import { useTracker } from '~/composables/tracker';
import { getTestPartFile, transformFilesToBase64 } from '~/modules/files';
import { useCountryStore } from '~/stores/countries';
import { useMeStore } from '~/stores/me';
import { useCartStore } from '~/stores/cart';
import { useFileStore } from '~/stores/file';
import { useLabStore } from '~/stores/lab';
import { useSettingsStore } from '~/stores/settings';
import { useReferentialStore } from '~/stores/referential';
import { checkFormatOfFile, checkSizeOfFile } from '~/modules/files_check';
import { useAlerts } from '~/composables/alerts';
import { requirejs } from '~/modules/require';
import { getIOSEnabledExtensions, isIOS } from '~/modules/platform';
import $fetch from '~/modules/fetch';

// PROPS
interface Props {
  displayUploadButons?: boolean;
  displaySharedParts?: boolean;
  displayUploadFeatures?: boolean;
  displayDeliveryDate?: boolean;
  dense?: boolean;
  hideTip?: boolean;
  // When true (adding a part from an existing cart), emit `uploaded`/`cloud-link-pressed`
  // for the parent to handle in-place instead of navigating to the quote edition page.
  addPart?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  displayUploadButons: true,
  displaySharedParts: true,
  displayUploadFeatures: true,
  displayDeliveryDate: true,
  dense: false,
  hideTip: true,
  addPart: false,
});

const emit = defineEmits(['uploaded', 'cloud-link-pressed']);

// VARIABLES
const { $i18n } = useTranslations('pages/upload');
const { $i18n: $i18nOrdering } = useTranslations('ordering');
const i18nAsync = useI18NAsync('pages/upload');
const countries = useCountryStore();
const me = useMeStore();
const cart = useCartStore();
const referential = useReferentialStore();
const settings = useSettingsStore();
const lab = useLabStore();
const fileStore = useFileStore();
const alert = useAlerts();

const MAX_FILES_NUMBER = 15;
const estimatedShippingCountry: Ref<string> = ref('');
const router = useRouter();
const tracker = useTracker();
const loading = ref(true);
const globalLoading = ref(false);
const input = ref();
const extensions = settings.getDriveEnabledExtensions();

const inputExtensionsIOs = getIOSEnabledExtensions().join();
const inputExtensions = settings
  .getDriveEnabledExtensions()
  .map((el) => `.${el}`)
  .join();
const isSharedPartCheckBoxChecked = ref(false);
let startedPreloading = false;

const noConversionsNeeded = (files: File[]) =>
  me.isLoggedIn ||
  files.every((file) => ['stl', 'obj'].includes((file.name.split('.').pop() || '').toLowerCase()));

// COMPUTED
const features = computed(
  () =>
    [
      {
        icon: 'credit-card',
        title: $i18n('payByCardTitle'),
        content: $i18n('payByCardContent'),
      },
      {
        icon: 'shield-full',
        title: $i18n('secureTransactionTitle'),
        content: $i18n('secureTransactionContent'),
      },
      {
        icon: 'lock',
        title: $i18n('dataAreSafeTitle'),
        content: $i18n('dataAreSafeContent'),
      },
    ] as UploadFeature[],
);

// METHODS
const fetchCountries = async () => {
  await countries.fetchCountries();
  me.setCountries(countries.countries);
  me.setEstimatedUserLocation(countries.estimatedUserLocation);

  // Local Estimated Shipping Country is the one on the Cart
  estimatedShippingCountry.value = cart.shippingAddressCode;
};

const selectCountry = async (isoCode: string) => {
  if (isoCode) {
    estimatedShippingCountry.value = isoCode;
    cart.setEstimationCountryShippingPrice(isoCode);
  }
};

const startPreloading = async () => {
  preloadDimensionCalculer();
};

const goToDrive = async () => {
  tracker?.event('browse_cloud_file_clicked', 'navigation', 'CLICK');

  if (isSharedPartCheckBoxChecked.value) {
    await me.setSharedParts(true);
  }

  if (props.addPart) {
    emit('cloud-link-pressed');
    return;
  }

  return router.push({ name: 'Quote3DDrive' });
};

/* Add a part to the existing cart: upload the files and let the parent add them in-place
 * (instead of navigating to the quote edition page, which is a no-op when already there). */
const uploadInPlace = async (rawFiles: File[]) => {
  const cleanFiles = await cleanUpIncompatiblesFiles(rawFiles);
  if (!cleanFiles.length) {
    return;
  }

  if (cleanFiles.length > MAX_FILES_NUMBER) {
    alert?.show({
      text: $i18n('error_max_files', { maxFilesNumber: MAX_FILES_NUMBER }),
      type: 'error',
      timeout: 5000,
    });
    return;
  }

  globalLoading.value = true;
  try {
    if (isSharedPartCheckBoxChecked.value) {
      await me.setSharedParts(true);
    }

    if (me.isLoggedIn) {
      const fcsFiles = await fileStore.uploadFilesToFCS(cleanFiles);
      emit('uploaded', fcsFiles);
    } else {
      const base64Files = await transformFilesToBase64(cleanFiles);
      emit('uploaded', base64Files, true);
    }
  } catch (e: any) {
    tracker?.error('ordering', 'HIGH', e?.message, 'add_part_upload_error');
    alert?.show({ text: $i18n('error_uploading_files'), type: 'error', timeout: 5000 });
  } finally {
    globalLoading.value = false;
  }
};

const useOurTestPart = async () => {
  tracker?.event('test_part_clicked', 'navigation', 'CLICK');
  globalLoading.value = true;

  cart.setTestPartMode(true);
  const file = await getTestPartFile();

  createCartFromLocalFiles([file], true);
};

const uploadFilesInLocalDB = async (files: File[], force?: boolean) => {
  fileStore.writeFilesInDB(files);
  if (noConversionsNeeded(files) || force) {
    router.push({ name: 'QuoteEdition', query: { ...getCustomPrintItemURLParams() } });
  } else {
    if (window.t0) {
      console.log('[DEBUG] Redirect to passport, performance timer will not be saved');
      delete window.t0;
    }

    redirectToPassport(true);
  }
};

const storeFilesInRAM = (files: File[]) => {
  fileStore.storeFilesInRAM(files);
};

const storeFilesInIndexedDB = async (files: File[]) => {
  const files64: File[] = (await transformFilesToBase64(files)) as File[];
  await uploadFilesInLocalDB(files64, true);
};

const chooseStorageForFiles = async (files: File[], force: boolean) => {
  if (noConversionsNeeded(files) || force) {
    if (window.t0) {
      console.log('[DEBUG] Using FAST RAM storage for files');
    }

    await storeFilesInRAM(files);
    await router.push({ name: 'QuoteEdition', query: { ...getCustomPrintItemURLParams() } });
  } else {
    if (window.t0) {
      console.log(
        '[DEBUG] Using Slower indexedDB storage for files because conversion of part is needed',
      );
    }

    await storeFilesInIndexedDB(files);
    redirectToPassport(true);
  }
};

// IDEA : Do it in $fetch Module
const redirectToPassport = async (isFromDragAndDrop: boolean = false) => {
  const url = new URL(
    window.location.origin.concat(
      String(router.getRoutes().find((e) => e.name === 'QuoteEdition')?.path),
    ),
  );
  if (isFromDragAndDrop) {
    url.searchParams.append('fileUpload', 'redirect');
  }

  tracker?.event('redirected_to_passport', '3D_file_upload', 'REDIRECTION', [], []);
  const redirection = encodeURIComponent(url.toString());
  const passportUrl = await $fetch.getServiceURL('3DPassport');
  window.location.assign(`${passportUrl}/login?service=${redirection}`);
};

const openLocalFiles = () => {
  tracker?.event('browse_local_files_clicked', '3D_file_upload', 'CLICK');
  input.value.click();
};

/* Upload Files from INPUT */
const uploadFromInput = async () => {
  const files = Array.from(input.value.files) as File[];

  if (files.length > MAX_FILES_NUMBER) {
    alert?.show({
      text: $i18n('error_max_files', { maxFilesNumber: MAX_FILES_NUMBER }),
      type: 'error',
      timeout: 5000,
    });
    return;
  }

  const totalSizeOfFiles = files.reduce((acc, el) => {
    acc = Number(el.size);
    return acc;
  }, 0);

  tracker?.event(
    'upload_local_files',
    '3D_file_upload',
    'Passive',
    [],
    [totalSizeOfFiles, files.length],
  );

  if (props.addPart) {
    await uploadInPlace(files);
  } else {
    createCartFromLocalFiles(files, me.isLoggedIn);
  }

  input.value.value = '';
};

/* DRAG AND DROP FILES */
const uploadFromDrop = async (e: DragEvent) => {
  const filesToUpload = Array.from(e.dataTransfer?.files || []);
  const cleanFiles = await cleanUpIncompatiblesFiles(filesToUpload);
  if (!cleanFiles.length) {
    return;
  }

  if (cleanFiles.length > MAX_FILES_NUMBER) {
    alert?.show({
      text: $i18n('error_max_files', { maxFilesNumber: MAX_FILES_NUMBER }),
      type: 'error',
      timeout: 5000,
    });
    return;
  }

  const totalSizeOfFiles = cleanFiles.reduce((acc, el) => {
    acc = Number(el.size);
    return acc;
  }, 0);
  tracker?.event(
    'drag_and_drop_files',
    '3D_file_upload',
    'DRAG&DROP',
    [],
    [totalSizeOfFiles, cleanFiles.length],
  );

  if (props.addPart) {
    await uploadInPlace(cleanFiles);
  } else {
    createCartFromLocalFiles(cleanFiles, me.isLoggedIn);
  }
};

const createCartLocal = async (files: File[], force: boolean) => {
  try {
    if (isSharedPartCheckBoxChecked.value) {
      await me.setSharedParts(true);
    }

    chooseStorageForFiles(files, force);
  } catch (e: any) {
    tracker?.error('ordering', 'HIGH', e.message, 'cart_creation_error');
  }
};

const createCartFromLocalFiles = async (files: File[], force: boolean) => {
  if (!files.length) {
    return;
  }

  const cleanFiles = await cleanUpIncompatiblesFiles(files);
  if (!cleanFiles.length) {
    return;
  }

  // Check if not production
  if (window.location.hostname !== 'make.3dexperience.3ds.com') {
    console.log('[DEBUG] Performance timer enabled');
    window.t0 = performance.now();
  }

  try {
    return createCartLocal(cleanFiles, force);
  } catch (e: unknown) {
    console.error(e);
    globalLoading.value = false;
    alert?.show({ text: $i18n('error_uploading_files'), type: 'error', timeout: 30000 });
  }
};

// Check if preselected process is compatible with file format
const checkFormatOfFileForProcess = (file: File, pf: string) => {
  const processFamiliesByFormat = referential.mapProcessFamiliesByFormat;
  const fileName: string = file?.name?.split('.')?.pop()?.toUpperCase() || '';

  return !processFamiliesByFormat[fileName].includes(pf);
};

const cleanUpIncompatiblesFiles = async (files: File[]) => {
  const filesToUpload: File[] = [];
  const pf = String(router.currentRoute.value.query.pf || '');

  if (pf) {
    await referential.fetchProcessFamilies();
  }

  files.forEach((file) => {
    if (!checkFormatOfFile(file, extensions)) {
      alert?.show({
        text: $i18n('select_errorFileFormatForFile', { file: file.name }),
        type: 'warning',
        timeout: 5000,
      });
    } else if (!checkSizeOfFile(file)) {
      alert?.show({
        text: $i18n('select_fileTooLarge', { file: file.name }),
        type: 'warning',
        timeout: 5000,
      });
    } else if (pf && checkFormatOfFileForProcess(file, pf)) {
      alert?.show({
        text: $i18n('select_errorFileFormatForFileForProcess', { file: file.name }),
        type: 'warning',
        timeout: 5000,
      });
    } else {
      filesToUpload.push(file);
    }
  });

  return filesToUpload;
};

const getCustomPrintItemURLParams = () => {
  const params = {
    pf: String(router.currentRoute.value.query.pf),
    pr: String(router.currentRoute.value.query.pr),
    mf: String(router.currentRoute.value.query.mf),
    mc: String(router.currentRoute.value.query.mc),
    mt: String(router.currentRoute.value.query.mt),
    mm: String(router.currentRoute.value.query.mm),
  };
  return Object.entries(params).reduce((a: Record<string, string>, [k, v]) => {
    if (!v || v === 'undefined') {
      return a;
    }

    a[k] = v;
    return a;
  }, {});
};

/** Preload DimensionCalculer which loads the quote-edit 3D Player */
const preloadDimensionCalculer = async () => {
  if (startedPreloading) {
    return;
  }

  me.fetchCountries();
  referential.fetchOffers();
  referential.fetchProcessFamilies();

  startedPreloading = true;
  me.fetchCountries();
  referential.fetchOffers();
  referential.fetchProcessFamilies();

  const dependencies = [
    'DS/Visualization/WebGLV6Viewer',
    'DS/Visualization/Viewpoint',
    'DS/MP3DPOrdering/pages/quote-edit',
    'DS/MP3DPCommonComponents/MP3DPDimensionCalculer',
    'DS/MP3DPApplicationInfra/MP3DPVueWidgetStarter',
    'DS/ENOPAD3DViewer/utils/PAD3DViewerModelServices',
    'DS/XCADLoader/XCADModelLoaderPlug',
    'UWA/Core',
    'DS/Visualization/Visualization',
    'DS/Visualization/Node3D',
    'DS/Visualization/ModelLoader',
    'DS/Visualization/SubElement',
    'DS/Visualization/ThreeJS_DS',
    'DS/MP3DPVisuServices/MP3DPModelInfoServices',
    'DS/MP3DPApplicationStore/MP3DPConversionHelpers',
    'DS/MP3DPApplicationStore/MP3DPConverter',
    'CSIMathematics/CSIDeclareMathematics',
    'DS/Visualization/Layers',
    'DS/GPURenderer/ComputeInterface',
    'DS/ShaderBuilders/ShaderBuilder',
    'DS/MP3DPOrdering/components/funnel',
    'DS/MP3DPSkeletons/part-content-skeleton',
    'DS/MP3DPSkeletons/lab-card-skeleton',
    'DS/MP3DPSkeletons/part-title-skeleton',
    'DS/MP3DPSkeletons/part-accordion-skeleton',
    'DS/Mesh/ThreeJS_Base',
    'DS/MP3DPOrdering/components/lab-card',
    'DS/MP3DPOrdering/components/upload',
    'DS/MP3DPOrdering/components/lab-selector',
    'DS/MP3DPCommonComponents/MP3DPPartControl',
    'DS/MP3DPCommonViews/MP3DPGeometryDefectsListView',
    'DS/MP3DPCommonComponents/MP3DPImage',
    'DS/MP3DPExternalWidget/MP3DPAddinPublicEvent',
    'DS/MP3DPExternalWidget/MP3DPExternalWidget',
    'DS/MP3DPOrdering/components/cart-recuperator',
    'DS/MP3DPCommonComponents/MP3DPRadialStepper',
    'DS/MP3DPOrdering/components/upload',
    'DS/MP3DPCommonComponents/MP3DPReadyPlayerOne',
  ];
  if (me.isLoggedIn) {
    const loggedInDeps = [
      'DS/MP3DCheckAndRepair/MP3DDFMCheck',
      'DS/MP3DCheckAndRepair/MP3DDFMCommunicationCSI',
      'DS/DFMCheckResultViewer/DFMCheckResultViewer',
      'DFMWebInfra/CSIDFMDeclareTypes',
      'DS/MP3DPCommonComponents/MP3DPDFMInformations',
    ];
    dependencies.push(...loggedInDeps);
  }

  await requirejs<[any, any, any, any, any, any, any, any, any, any, any, any, any, any]>(
    dependencies,
  );
  console.log('[DEBUG] Preloading of 3DPlayer finished');
};

const idleDimensionCalculer = () => {
  if (window.requestIdleCallback) {
    window.requestIdleCallback(() => {
      setTimeout(() => preloadDimensionCalculer(), 1000);
    });
  } else {
    setTimeout(() => preloadDimensionCalculer(), 1000);
  }
};

// We download the document sent in URL and create a CART with IT
const createCartFromURL = async (documentURL: string, name: string) => {
  try {
    globalLoading.value = true;
    cart.resetCart();

    const document = (await $fetch.get(documentURL, { file: true })) as Blob;
    const file = new File([document], name);
    globalLoading.value = false;

    tracker?.event(
      'create_cart_from_outer_url',
      'navigation',
      'PASSIVE',
      ['', '', '', '', '', documentURL, ''],
      [file.size],
    );

    if (!me.isLoggedIn) {
      const savedFiles = (await transformFilesToBase64([file])) as File[];
      return uploadFilesInLocalDB(savedFiles);
    }

    return createCartFromLocalFiles([file], me.isLoggedIn);
  } catch (e: any) {
    globalLoading.value = false;

    tracker?.error('ordering', 'HIGH', e.message, 'cart_creation_from_outer_url', [
      '',
      '',
      '',
      '',
      '',
      documentURL,
    ]);
    alert?.show({ text: $i18n('error_uploading_files'), type: 'error', timeout: 5000 });
  }
};

const setupStoreMode = async () => {
  referential.fetchOffers();

  try {
    await lab.fetchLab({ id: String(router.currentRoute.value.params?.id), uc2: false });
    cart.setExploreMode(true);
    cart.setSelectedLabs([{ id: lab.lab?.id }]);

    if (me.isLoggedIn) {
      await cart.waitLabsAreLoaded();
    }
  } catch (e) {
    alert?.show({
      text: e.message === 'Lab not found' ? $i18n('lab_not_found') : $i18n('error_fetching_lab'),
      type: 'error',
      timeout: 10000,
    });
  }
};

const resetCartExploreMode = async () => {
  const previousPage = router.options.history.state.back as string;
  // ResetCart in case of explore mode returning to QuoteUpload
  if (
    previousPage?.includes('/labs') ||
    (previousPage?.includes('/quote/edit') && router.currentRoute.value.name !== 'Home')
  ) {
    cart.resetCart({ noResetExploreMode: true, noResetLabs: true });
  } else {
    // Also reset explore mode
    cart.resetCart();
  }
};

onMounted(async () => {
  loading.value = true;
  if (router.currentRoute.value.name !== 'Home') {
    await Promise.all([fetchCountries(), me.waitForMeSynchro(), me.fetchMe()]);

    if (
      router.currentRoute.value.query?.documentURL &&
      router.currentRoute.value.query?.documentName
    ) {
      return createCartFromURL(
        router.currentRoute.value.query?.documentURL as string,
        router.currentRoute.value.query?.documentName as string,
      );
    }

    const isExploreMode = cart.exploreMode;

    if (!isExploreMode) {
      // Reset Cart for all states but explore mode
      // TODO: this resetCart break everything, to investigate... cart.resetCart();
    } else if (isExploreMode && cart.cart) {
      if (router.currentRoute.value.name !== 'QuoteEdition') {
        resetCartExploreMode();
      }
    }

    // Setup UI for Explore Mode
    if (router.currentRoute.value.name === 'QuoteUploadWithSelectedLab') {
      setupStoreMode();
    }

    idleDimensionCalculer();
  }

  loading.value = false;
});
</script>

<template>
  <div
    @click="startPreloading"
    :class="[
      'upload-container flex flex-column grid-gap-10px grey-7--text relative border-thin grey-4--border',
      {
        dense: dense,
        loggedIn: me.isLoggedIn,
        loading: loading,
      },
    ]"
    v-mask="globalLoading"
  >
    <div class="inner-container">
      <div v-if="props.dense" class="dense-toolbar flex">
        <div class="title">
          {{ $i18nOrdering('upload_addFiles') }}
        </div>
        <div :class="['btns-container grid grid-2-columns grid-1-columns--xs']">
          <a
            :class="
              settings.isMobileDevice ? 'btn btn-default btn-block' : 'btn-link-icon text-right'
            "
            @click="openLocalFiles()"
          >
            <span class="fonticon fonticon-survey" />&nbsp;
            <span class="text">{{ $i18nOrdering('upload_browseLocalFile') }}</span></a
          >
          <a
            :class="[
              'to-cloud-drive',
              settings.isMobileDevice ? 'btn btn-default btn-block' : 'btn-link-icon text-right',
              { 'disabled-link': !me.isLoggedIn },
            ]"
            @click="me.isLoggedIn ? goToDrive() : null"
          >
            <span class="fonticon fonticon-cloud-download" />&nbsp;
            <span class="text">{{ $i18nOrdering('upload_cloudDrives') }}</span></a
          >
        </div>
        <div v-if="settings.isMobileDevice && !props.dense" class="formats-tips">
          {{
            $i18nOrdering('upload_formatAndSizeTips', {
              formats: extensions.join(', ').toUpperCase(),
              size: 128,
            })
          }}
          {{ $i18nOrdering('upload_max50Mo') }}
        </div>
      </div>
      <DragAndDropUpload
        v-if="!settings.isMobileDevice"
        @select-local-files="openLocalFiles()"
        @drag-and-drop-files="(e) => uploadFromDrop(e)"
        :disabled="loading"
        :dense="props.dense"
        :hide-tip="props.hideTip"
      >
        <template #title>
          <slot name="title" />
        </template>
        <template #tips>
          <div class="format-tips grey-6--text text-10 mb-10px text-center">
            <i18nAsync ikey="compatiblesFormats" />
          </div>
        </template>
      </DragAndDropUpload>
    </div>
    <div
      v-if="props.displayUploadButons"
      class="flex flex-column--sm flex-row--md align-items-center grid-gap-10px mx-30px mx-20px--sm upload-btns-container"
    >
      <CountrySelector
        id="country-selector"
        class="flex-order-10--sm"
        :countries="countries.$state.countries"
        :display-delivery-date="props.displayDeliveryDate"
        @select-country="(e) => selectCountry(e)"
      />
      <vu-btn
        v-if="settings.isMobileDevice"
        @click="openLocalFiles()"
        color="primary"
        class="btn-block"
        :disabled="loading"
      >
        <vu-icon icon="upload" color="white--text" />
        {{ $i18n('uploadLocalFiles') }}
      </vu-btn>
      <vu-btn
        @click="goToDrive"
        :class="[{ 'btn-block': settings.isMobileDevice }]"
        :disabled="loading"
        id="goToDrive"
      >
        <vu-icon icon="cloud" />
        {{ $i18n('browse3DDrive') }}
      </vu-btn>
      <vu-btn
        @click="useOurTestPart()"
        :class="[{ 'btn-block': settings.isMobileDevice }]"
        :disabled="loading"
        id="goToTestPart"
      >
        <vu-icon icon="3d-object" />
        {{ $i18n('useTestPart') }}
      </vu-btn>
      <div
        class="format-tips grey-6--text text-12 mb-10px text-center"
        v-if="settings.isMobileDevice"
      >
        {{ $i18n('compatiblesFormats') }}
      </div>
    </div>
    <SharedParts
      v-if="props.displaySharedParts && me.isLoggedIn"
      class="mx-40px mx-20px--sm grey-6--text"
      :shared-part-active="isSharedPartCheckBoxChecked"
      :active="me.isSharedPartsAccepted"
      @update:shared-parts-active="isSharedPartCheckBoxChecked = !isSharedPartCheckBoxChecked"
    />
    <div
      v-if="props.displayUploadFeatures"
      class="grid grid-3-columns grid-1-columns--sm grid-gap-15px bottom-of-card mx-20px mb-20px"
    >
      <UploadFeatureComponent v-for="feature in features" :key="feature.title" :feature="feature" />
    </div>

    <ClientOnly>
      <input
        ref="input"
        class="hidden file-upload-input"
        type="file"
        multiple
        :accept="isIOS() ? inputExtensionsIOs : inputExtensions"
        @change="uploadFromInput"
      />
    </ClientOnly>
  </div>
</template>

<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;

.upload-btns-container {
  .btn {
    @include variables.for-sm {
      padding: 15px;
    }
  }
}

#country-selector {
  @include variables.for-sm {
    width: 100%;
  }
}

.bottom-of-card {
  border-top: solid 1px #b4b6ba;
  padding-top: 15px;
  font-size: 13px;
}

.upload-container {
  margin: auto;
  background-color: white;
  position: relative;
  border-radius: 20px;

  &:not(.dense) {
    width: 100%;
    max-width: 1084px;
  }

  &.dense .progress-bar-container .progress {
    @media screen and (min-width: variables.$screen-sm-max) {
      position: relative;
      top: 0px;
      margin-inline: 40px;
    }
  }

  &.dense {
    .inner-container {
      padding: 0 20px 20px;
      max-width: unset;

      @media screen and (max-width: variables.$screen-sm-max) {
        padding: 0 10px 10px;
      }

      .formats-tips {
        margin-top: 10px;
        font-size: 12px;
      }

      .drop-zone {
        min-height: 105px;

        .icon-container .fonticon {
          font-size: 38px;
        }
      }
    }
  }

  .disabled-link {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }

  .inner-container {
    width: 100%;
    padding: 0;
    margin: auto;

    @media screen and (max-width: variables.$screen-sm-max) {
      padding: 20px;
    }

    .dense-toolbar {
      justify-content: space-between;
      align-items: center;

      @media screen and (max-width: variables.$screen-sm-max) {
        display: unset;
        justify-content: unset;
        align-items: unset;
      }

      .title {
        color: variables.$grey-6;
        font-weight: bold;
        line-height: 38px;
        font-size: 13px;
      }

      .btns-container {
        gap: 30px;

        @media screen and (max-width: variables.$screen-sm-max) {
          gap: 8px;
        }
      }
    }
  }
}
</style>
